import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Skeleton, Typography, Unstable_Grid2 as Grid2} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {CustomerGiftCardTypeIds} from "../../../../../../../core/constants/enums";
import Api from "../../../../../../../core/services/api/api";
import GiftCard from "../../../../../../components/app-specific/payment-cards/gift-card";
import CreateGiftCardDialog from "../../../../../../components/dialogs/create-gift-card";
import RefillGiftCardModal from "../../../../../../components/dialogs/refill-gift-card";

const initNewGiftCardDialogState = {open: false, close: () => void 0, onSuccess: () => void 0}
const initRefillGiftCardDialogState = {open: false, close: () => void 0, giftCard: null, onSuccess: () => void 0}

const CustomerPaymentMethodsViewGiftCardsSection = ({customer}) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [newGiftCardDialogState, setNewGiftCardDialogState] = useState(initNewGiftCardDialogState);
    const [refillGiftCardDialogState, setRefillGiftCardDialogState] = useState(initRefillGiftCardDialogState);
    const isMounted = useIsMounted();

    const allowCreationOfOnlyFixedGiftCards = useMemo(() => !!data?.find(e => e.type?.id === CustomerGiftCardTypeIds.refillable), [data])

    /**
     * Fetches the list of gift cards for a specific user form the server. if the component is mounted and the api
     * result is successful, then sets the list.
     * @type {() => Promise<void>}
     */
    const getGiftCards = useCallback(async () => {
        setLoading(true);
        const response = await Api.getCustomerGiftCards(customer?.id);
        if (!isMounted())
            return;
        setData(response?.data ?? []);
        setLoading(false);
    }, [isMounted, customer?.id])

    /**
     * Fetches the list of gift cards for a specific user form the server. if the component is mounted and the api
     */
    useEffect(() => {
        void getGiftCards();
    }, [getGiftCards])

    /**
     * Removes a gift card from a list of gift cards by calling the server and only updating the state if the result
     * of the api call is successful.
     * @type {(giftCard: any) => Promise<void>}
     */
    const removeGiftCard = useCallback(async (giftCard) => {
        const response = await Api.removeGiftCard(giftCard.cardNumber);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.cardNumber !== giftCard.cardNumber))
        }
    }, [isMounted])

    /**
     * Opens the dialog for refilling a gift card.
     * @type {(function(*): void)}
     */
    const openRefillGiftCard = useCallback((giftCard) => {
        setRefillGiftCardDialogState({
            open: true,
            close: () => setRefillGiftCardDialogState(initRefillGiftCardDialogState),
            giftCard,
            onSuccess: (giftCard) => setData(prevState => prevState.map(e => e.cardNumber === giftCard.cardNumber ? giftCard : e)),
        });
    }, [])

    /**
     * Opens the dialog for adding a new gift card.
     * @type {(function(): void)}
     */
    const openNewGiftCardDialog = useCallback(() => {
        setNewGiftCardDialogState({
            open: true,
            close: () => setNewGiftCardDialogState(initNewGiftCardDialogState),
            onSuccess: (giftCard) => {
                setData(prevState => {
                    const index = prevState.findIndex(e => e.cardNumber === giftCard.cardNumber);
                    if (index === -1) {
                        return [giftCard, ...prevState];
                    }
                    prevState.splice(Math.max(index - 1, 0), 0, [giftCard]);
                    return prevState;
                })
            },
        })
    }, [])

    const sections = useMemo(() => Object.values(data.reduce((acc, card) => {
        const groupId = card.type?.id ?? "no-type";
        acc[groupId] = {
            id: groupId,
            title: card.type?.name ?? "",
            data: [...(acc[groupId]?.data ?? []), card]
        }
        return acc;
    }, {})), [data])

    return (
        <>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBlockEnd: 2}}>
                <Typography variant={'h6'} fontWeight={600}>
                    {customer?.name ?? "Customer"}'s Gift Cards
                </Typography>
                <Button
                    variant={'outlined'}
                    color={'primary'}
                    disabled={loading}
                    onClick={openNewGiftCardDialog}
                >
                    Add Gift Card
                </Button>
            </Box>
            <Grid2 container spacing={2} marginBlock={0}>
                {
                    loading &&
                    <>
                        <Grid2 xs={12}>
                            <Skeleton sx={{transform: 'none'}} height={30} width={'50%'}/>
                        </Grid2>
                        {
                            Array.from({length: 3}).map((_, index) => (
                                <Grid2 xs={'auto'} key={`first-${index}`}>
                                    <Skeleton
                                        sx={{transform: 'none'}}
                                        height={270}
                                        width={312}
                                    />
                                </Grid2>
                            ))
                        }
                        <Grid2 xs={12}>
                            <Skeleton sx={{transform: 'none'}} height={30} width={'50%'}/>
                        </Grid2>
                        {
                            Array.from({length: 3}).map((_, index) => (
                                <Grid2 xs={'auto'} key={`second-${index}`}>
                                    <Skeleton
                                        sx={{transform: 'none'}}
                                        height={270}
                                        width={312}
                                    />
                                </Grid2>
                            ))
                        }
                    </>
                }
                {
                    !loading &&
                    data?.length > 0 &&
                    sections.map((entry) => (
                        <Grid2 key={entry.id} xs={12}>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    {entry.title ?? ""}
                                </Typography>
                                <Grid2 container spacing={2}>
                                    {
                                        entry.data?.map(giftCard => (
                                            <Grid2 xs={'auto'} key={giftCard.cardNumber}>
                                                <GiftCard
                                                    withWrapper
                                                    card={giftCard}
                                                    remove={() => removeGiftCard(giftCard)}
                                                    customerId={customer?.id}
                                                    {...(giftCard?.type?.id === CustomerGiftCardTypeIds.refillable && {
                                                        buttonText: 'Refill',
                                                        onButtonPressed: openRefillGiftCard,
                                                    })}
                                                />
                                            </Grid2>
                                        ))
                                    }
                                </Grid2>
                            </Box>
                        </Grid2>
                    ))
                }
                {
                    !loading && data?.length === 0 &&
                    <Grid2 xs={12}>
                        <Typography variant={'body2'} marginBlock={10} textAlign={'center'} width={'100%'}>
                            This customer does not have any gift cards saved in their account.
                            Try adding one for them.
                        </Typography>
                    </Grid2>
                }
            </Grid2>
            <CreateGiftCardDialog
                open={newGiftCardDialogState.open}
                setOpen={newGiftCardDialogState.close}
                onSuccess={newGiftCardDialogState.onSuccess}
                customerId={customer?.id}
                onlyFixed={allowCreationOfOnlyFixedGiftCards}
            />
            <RefillGiftCardModal
                open={refillGiftCardDialogState.open}
                setOpen={refillGiftCardDialogState.close}
                giftCard={refillGiftCardDialogState.giftCard}
                onSuccess={refillGiftCardDialogState.onSuccess}
            />
        </>
    );
}

export default CustomerPaymentMethodsViewGiftCardsSection;
